import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { Text, TextInput } from '@/shared/ui';
import { Select } from '@/shared/ui/components/form/inputs/Select';
import { cn } from '@/shared/ui/utils/tailwind';
import { DealDrawerSubmitButton } from './DealDrawerSubmitButton';
import { LOST_REASONS, useLostReasonLabels, type LostReason } from '../constants/lost-reasons';

interface LoseDealDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: LostReason, note: string) => Promise<void>;
}

const labelClasses = cn(
  'text-[length:var(--text-meta)]',
  'font-[weight:var(--font-medium)]',
  'text-(--text-secondary)',
);

/**
 * Dialog collecting the loss reason for closing a deal as lost
 * [Plano 3, Fase 4, SC-2] -- feeds the dashboard's loss-reasons KPI.
 *
 * The reason is picked from a closed vocabulary because the KPI groups
 * by it; the note beside it is free text and is never aggregated, so it
 * can carry the context the vocabulary cannot without splitting a cause
 * into several entries.
 *
 * @param props - Dialog open state and submit/close callbacks.
 * @returns The lose-deal form shell.
 */
export function LoseDealDialog({ open, onClose, onSubmit }: LoseDealDialogProps) {
  const { t } = useTranslation('features/deals');
  const reasonLabels = useLostReasonLabels();
  const reasonOptions = useMemo(
    () => LOST_REASONS.map((value) => ({ value, label: reasonLabels[value] })),
    [reasonLabels],
  );
  const [reason, setReason] = useState<LostReason | ''>('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = reason !== '';

  const handleSubmit = async () => {
    if (reason === '') return;
    setSubmitting(true);
    try {
      await onSubmit(reason, note.trim());
      setReason('');
      setNote('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={t('detail.loseDialog.title')}
      density="compact"
      footer={
        <DealDrawerSubmitButton
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
          loading={submitting}
          label={t('detail.loseDialog.confirm')}
          savingLabel={t('detail.loseDialog.confirming')}
        />
      }
    >
      <div data-testid="lose-deal-form" className="flex flex-col gap-(--space-4)">
        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" htmlFor="lose-reason" className={labelClasses}>
            {t('detail.loseDialog.reason')}
          </Text>
          <Select
            id="lose-reason"
            name="lostReason"
            data-testid="lose-reason-select"
            value={reason}
            onChange={(value) => setReason(String(value ?? '') as LostReason | '')}
            placeholder={t('detail.loseDialog.reasonPlaceholder')}
            data={reasonOptions}
          />
        </fieldset>

        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" htmlFor="lose-note" className={labelClasses}>
            {t('detail.loseDialog.note')}
          </Text>
          <TextInput
            id="lose-note"
            data-testid="lose-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('detail.loseDialog.notePlaceholder')}
          />
        </fieldset>
      </div>
    </AdaptiveFormShell>
  );
}
